'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { CAREER_PATHWAYS, DEVELOPMENTAL_LEVELS, SUBJECTS, type CareerTrack, type DevelopmentalLevel } from '@/lib/curriculum-data';

const QUIZ_QUESTIONS = [
  { q: 'What subject do you enjoy most?', options: ['Mathematics & Science', 'Languages & Writing', 'Art & Design', 'Helping People', 'Technology & Computers', 'Business & Finance'] },
  { q: 'What type of work environment do you prefer?', options: ['Hospital or clinic', 'Office or corporate', 'Outdoors or field work', 'Studio or creative space', 'School or community', 'Remote / flexible'] },
  { q: 'What motivates you most?', options: ['Helping others', 'Building things', 'Creating art', 'Making money', 'Seeking knowledge', 'Solving problems'] },
];

const STUDY_PLAN_TEMPLATES = [
  { id: 'exam_prep', label: 'Exam Preparation', icon: '📝', duration: '4 weeks', description: 'Intensive revision plan for upcoming exams' },
  { id: 'new_subject', label: 'New Subject Mastery', icon: '📚', duration: '12 weeks', description: 'Structured learning for a new subject from scratch' },
  { id: 'career_switch', label: 'Career Transition', icon: '🚀', duration: '6 months', description: 'Comprehensive plan for changing career paths' },
  { id: 'certification', label: 'Certification Prep', icon: '🏆', duration: '8 weeks', description: 'Focused preparation for professional certifications' },
  { id: 'daily_learning', label: 'Daily Learning Habit', icon: '🌅', duration: 'Ongoing', description: '30-minute daily learning routine' },
  { id: 'hifdh', label: 'Hifdh Programme', icon: '📿', duration: '2 years', description: 'Structured Quran memorization plan' },
];

const GENERATED_PLAN = {
  title: 'High School Mathematics — Exam Preparation',
  duration: '4 weeks',
  dailyTime: '45 minutes',
  weeks: [
    {
      week: 1,
      theme: 'Algebra & Functions',
      days: [
        { day: 'Monday', topic: 'Linear Equations Review', duration: '45 min', type: 'Video + Practice' },
        { day: 'Tuesday', topic: 'Quadratic Equations', duration: '45 min', type: 'Lesson + Quiz' },
        { day: 'Wednesday', topic: 'Functions & Graphs', duration: '45 min', type: 'Interactive Lab' },
        { day: 'Thursday', topic: 'Simultaneous Equations', duration: '45 min', type: 'Worksheet' },
        { day: 'Friday', topic: 'Week 1 Review + Dua', duration: '30 min', type: 'Assessment' },
      ],
    },
    {
      week: 2,
      theme: 'Geometry & Trigonometry',
      days: [
        { day: 'Monday', topic: 'Angles & Triangles', duration: '45 min', type: 'Video + Practice' },
        { day: 'Tuesday', topic: 'Pythagoras Theorem', duration: '45 min', type: 'Lesson + Quiz' },
        { day: 'Wednesday', topic: 'Trigonometric Ratios', duration: '45 min', type: 'Interactive Lab' },
        { day: 'Thursday', topic: 'Circle Theorems', duration: '45 min', type: 'Worksheet' },
        { day: 'Friday', topic: 'Week 2 Review', duration: '30 min', type: 'Assessment' },
      ],
    },
  ],
};

export default function CareerAdvisorPage() {
  const [activeTab, setActiveTab] = useState<'advisor' | 'planner' | 'pathways'>('advisor');
  const [selectedCareer, setSelectedCareer] = useState<CareerTrack | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<DevelopmentalLevel>('high_school');
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<string[]>([]);
  const [showPlan, setShowPlan] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const pathway = selectedCareer ? CAREER_PATHWAYS.find(p => p.id === selectedCareer) : null;

  return (
    <AppLayout activeRoute="/career-advisor">
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-800 text-foreground">Career Advisor & Study Planner</h1>
          <p className="text-sm text-muted-foreground mt-1">Discover your ideal career pathway and build a personalized study plan</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 w-fit">
          {[
            { id: 'advisor', label: '🚀 Career Advisor' },
            { id: 'planner', label: '📅 Study Planner' },
            { id: 'pathways', label: '🗺️ All Pathways' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 rounded-lg text-sm font-600 transition-all ${activeTab === tab.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Career Advisor Tab */}
        {activeTab === 'advisor' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-card rounded-2xl border border-border p-5">
                <h2 className="font-800 text-foreground mb-4">Career Discovery Quiz</h2>
                {quizStep < QUIZ_QUESTIONS.length ? (
                  <div>
                    <p className="text-sm font-600 text-foreground mb-3">
                      Question {quizStep + 1} of {QUIZ_QUESTIONS.length}: {QUIZ_QUESTIONS[quizStep].q}
                    </p>
                    <div className="space-y-2">
                      {QUIZ_QUESTIONS[quizStep].options.map((opt, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            setQuizAnswers(prev => [...prev, opt]);
                            setQuizStep(prev => prev + 1);
                          }}
                          className="w-full text-left px-3 py-2.5 rounded-xl border border-border bg-muted hover:bg-primary/10 hover:border-primary/40 text-sm text-foreground transition-all"
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="text-4xl mb-3">🎯</div>
                    <p className="font-700 text-foreground mb-2">Quiz Complete!</p>
                    <p className="text-sm text-muted-foreground mb-4">Based on your answers, here are your top career matches:</p>
                    <div className="space-y-2 text-left">
                      {['Medicine & Health Sciences ⚕️', 'Education & Teaching 🎓', 'Global Development 🌍'].map((career, i) => (
                        <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${i === 0 ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-muted border-border text-foreground'}`}>
                          <span className="text-sm font-600">{i + 1}.</span>
                          <span className="text-sm font-600">{career}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => { setQuizStep(0); setQuizAnswers([]); }}
                      className="mt-4 text-xs text-primary font-600 hover:underline"
                    >
                      Retake quiz
                    </button>
                  </div>
                )}
              </div>

              {/* Level Selector */}
              <div className="bg-card rounded-2xl border border-border p-5">
                <h3 className="font-700 text-sm text-foreground mb-3">My Current Level</h3>
                <div className="space-y-1">
                  {(['elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'] as DevelopmentalLevel[]).map(level => {
                    const info = DEVELOPMENTAL_LEVELS[level];
                    return (
                      <button
                        key={level}
                        onClick={() => setSelectedLevel(level)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-600 transition-all ${selectedLevel === level ? 'bg-primary text-white' : 'bg-muted text-foreground hover:bg-primary/10'}`}
                      >
                        <span>{info.emoji}</span>
                        <span>{info.label}</span>
                        <span className="ml-auto opacity-70">{info.ageRange}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Career Pathways Grid */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="font-800 text-foreground">Explore Career Pathways</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {CAREER_PATHWAYS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedCareer(selectedCareer === p.id ? null : p.id)}
                    className={`text-left bg-card rounded-2xl border p-4 hover:shadow-md transition-all ${selectedCareer === p.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{p.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-700 text-sm text-foreground">{p.label}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                        {p.salaryRange && (
                          <p className="text-xs text-emerald-600 font-600 mt-1">💰 {p.salaryRange}</p>
                        )}
                        {p.certifications && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {p.certifications.slice(0, 2).map((cert, i) => (
                              <span key={i} className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-lg px-1.5 py-0.5 font-500">{cert}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Selected Pathway Detail */}
              {pathway && (
                <div className="bg-gradient-to-br from-primary/5 to-teal/5 rounded-2xl border border-primary/20 p-5">
                  <h3 className="font-800 text-foreground mb-3">{pathway.emoji} {pathway.label} — {DEVELOPMENTAL_LEVELS[selectedLevel].label} Pathway</h3>
                  <div className="bg-card rounded-xl p-4 border border-border">
                    <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-2">Topics at this level:</p>
                    <div className="flex flex-wrap gap-2">
                      {(pathway.levelProgression[selectedLevel] || ['Curriculum being developed']).map((topic, i) => (
                        <span key={i} className="text-xs bg-primary/10 text-primary border border-primary/20 rounded-xl px-3 py-1 font-500">
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <p className="text-xs font-700 text-foreground w-full">Core Subjects:</p>
                    {pathway.coreSubjects.map(sid => {
                      const sub = SUBJECTS.find(s => s.id === sid);
                      return sub ? (
                        <span key={sid} className="text-xs bg-card border border-border rounded-xl px-2 py-1 font-500 text-foreground">
                          {sub.emoji} {sub.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Study Planner Tab */}
        {activeTab === 'planner' && (
          <div className="space-y-6">
            {!showPlan ? (
              <div>
                <h2 className="font-800 text-foreground mb-4">Choose a Study Plan Template</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {STUDY_PLAN_TEMPLATES.map(template => (
                    <button
                      key={template.id}
                      onClick={() => { setSelectedTemplate(template.id); setShowPlan(true); }}
                      className="text-left bg-card rounded-2xl border border-border p-5 hover:shadow-md hover:border-primary/40 transition-all group"
                    >
                      <span className="text-3xl">{template.icon}</span>
                      <h3 className="font-700 text-foreground mt-3">{template.label}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                        <span className="text-xs font-600 text-primary">{template.duration}</span>
                        <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">Generate plan →</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-800 text-foreground">{GENERATED_PLAN.title}</h2>
                  <button onClick={() => setShowPlan(false)} className="text-xs text-primary font-600 hover:underline">← Back to templates</button>
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2">
                    <p className="text-xs text-blue-600">Duration</p>
                    <p className="font-700 text-blue-700">{GENERATED_PLAN.duration}</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2">
                    <p className="text-xs text-emerald-600">Daily Time</p>
                    <p className="font-700 text-emerald-700">{GENERATED_PLAN.dailyTime}</p>
                  </div>
                </div>
                {GENERATED_PLAN.weeks.map(week => (
                  <div key={week.week} className="bg-card rounded-2xl border border-border p-5">
                    <h3 className="font-800 text-foreground mb-3">Week {week.week}: {week.theme}</h3>
                    <div className="space-y-2">
                      {week.days.map((day, i) => (
                        <div key={i} className="flex items-center gap-4 p-3 bg-muted rounded-xl">
                          <span className="text-xs font-700 text-muted-foreground w-20 flex-shrink-0">{day.day}</span>
                          <span className="text-sm font-600 text-foreground flex-1">{day.topic}</span>
                          <span className="text-xs text-muted-foreground">{day.duration}</span>
                          <span className="text-xs bg-primary/10 text-primary rounded-lg px-2 py-0.5 font-500 flex-shrink-0">{day.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <button className="w-full bg-primary text-white font-700 py-3 rounded-xl hover:bg-primary/90 transition-colors">
                  Save Study Plan & Set Reminders
                </button>
              </div>
            )}
          </div>
        )}

        {/* All Pathways Tab */}
        {activeTab === 'pathways' && (
          <div className="space-y-4">
            <h2 className="font-800 text-foreground">All Career Pathways ({CAREER_PATHWAYS.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {CAREER_PATHWAYS.map(p => (
                <div key={p.id} className="bg-card rounded-2xl border border-border p-5 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl">{p.emoji}</span>
                    <div>
                      <h3 className="font-700 text-sm text-foreground">{p.label}</h3>
                      {p.salaryRange && <p className="text-xs text-emerald-600 font-600">{p.salaryRange}</p>}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{p.description}</p>
                  {p.certifications && (
                    <div className="flex flex-wrap gap-1">
                      {p.certifications.slice(0, 3).map((cert, i) => (
                        <span key={i} className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-lg px-1.5 py-0.5 font-500">{cert}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-[10px] font-700 text-muted-foreground uppercase tracking-wide mb-1">Core Subjects</p>
                    <div className="flex flex-wrap gap-1">
                      {p.coreSubjects.slice(0, 3).map(sid => {
                        const sub = SUBJECTS.find(s => s.id === sid);
                        return sub ? <span key={sid} className="text-[10px] bg-muted text-foreground rounded-lg px-1.5 py-0.5">{sub.emoji} {sub.name}</span> : null;
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
